import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { isMobile } from "react-device-detect";
import axiosInstance from "../../axiosConfig";
import {
  defaultValue,
  isInputField,
  isMultiValue,
} from "../../utils/dynamicForm";
import { FormLabelText, isBoxedParagraph } from "../../utils/formLabel";

const REQUIRED_MESSAGE = "هذا الحقل مطلوب";

const optionsOf = (field) => (Array.isArray(field.values) ? field.values : []);

export const StaticField = ({ field }) => {
  if (field.type === "header") {
    // formBuilder headers carry h1..h6 in subtype; map them onto the page's heading scale
    const variant = ["h1", "h2", "h3", "h4", "h5", "h6"].includes(field.subtype)
      ? { h1: "h5", h2: "h6", h3: "subtitle1", h4: "subtitle1", h5: "subtitle2", h6: "subtitle2" }[field.subtype]
      : "h6";
    return (
      <Typography variant={variant} sx={{ fontWeight: "bold", color: "primary.main" }}>
        <FormLabelText html={field.label} />
      </Typography>
    );
  }
  if (isBoxedParagraph(field)) {
    // The staff screen draws these as a bootstrap box (e.g. a declaration the applicant agrees to)
    return (
      <Box
        sx={{
          p: 2.5,
          borderRadius: "8px",
          border: "1px solid #cfdcec",
          backgroundColor: "#f3f7fc",
          lineHeight: 2,
          "& strong:first-of-type": { display: "inline-block", fontSize: "1.15rem", color: "primary.main", mb: 1 },
        }}
      >
        <Typography component="div" sx={{ whiteSpace: "pre-line", lineHeight: 2 }}>
          <FormLabelText html={field.label} />
        </Typography>
      </Box>
    );
  }
  return (
    <Typography sx={{ whiteSpace: "pre-line" }}>
      <FormLabelText html={field.label} />
    </Typography>
  );
};

export const InputField = ({ field, index, control, errors }) => {
  const name = `dynamic.f${index}`;
  const error = errors?.dynamic?.[`f${index}`];
  const helperText = error?.message || field.description || "";
  const rules = {};

  if (field.required) {
    rules.required = REQUIRED_MESSAGE;
    if (isMultiValue(field)) {
      rules.validate = (value) =>
        (Array.isArray(value) && value.length > 0) || REQUIRED_MESSAGE;
    }
  }
  if (field.type === "number") {
    if (field.min !== undefined && field.min !== "") {
      rules.min = { value: Number(field.min), message: `القيمة الدنيا ${field.min}` };
    }
    if (field.max !== undefined && field.max !== "") {
      rules.max = { value: Number(field.max), message: `القيمة العليا ${field.max}` };
    }
  }
  if (field.maxlength) {
    rules.maxLength = {
      value: Number(field.maxlength),
      message: `لا يمكن أن يتجاوز ${field.maxlength} حرف`,
    };
  }

  const label = (
    <>
      <FormLabelText html={field.label} />
      {field.required && <span style={{ color: "red" }}> *</span>}
    </>
  );

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue(field)}
      rules={rules}
      render={({ field: input }) => {
        switch (field.type) {
          case "select":
          case "autocomplete":
            return (
              <TextField
                select
                fullWidth
                label={label}
                value={input.value ?? (isMultiValue(field) ? [] : "")}
                onChange={input.onChange}
                onBlur={input.onBlur}
                SelectProps={{ multiple: isMultiValue(field) }}
                error={!!error}
                helperText={helperText}
              >
                {optionsOf(field).map((option, i) => (
                  <MenuItem key={`${option.value}-${i}`} value={String(option.value ?? "")}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            );

          case "checkbox-group": {
            const checked = Array.isArray(input.value) ? input.value : [];
            return (
              <FormControl error={!!error} component="fieldset">
                <FormLabel component="legend">{label}</FormLabel>
                <FormGroup>
                  {optionsOf(field).map((option, i) => {
                    const value = String(option.value ?? "");
                    return (
                      <FormControlLabel
                        key={`${value}-${i}`}
                        label={option.label}
                        control={
                          <Checkbox
                            checked={checked.includes(value)}
                            onChange={(e) =>
                              input.onChange(
                                e.target.checked
                                  ? [...checked, value]
                                  : checked.filter((v) => v !== value)
                              )
                            }
                          />
                        }
                      />
                    );
                  })}
                </FormGroup>
                {helperText && <FormHelperText>{helperText}</FormHelperText>}
              </FormControl>
            );
          }

          case "radio-group":
            return (
              <FormControl error={!!error} component="fieldset">
                <FormLabel component="legend">{label}</FormLabel>
                <RadioGroup value={input.value ?? ""} onChange={input.onChange}>
                  {optionsOf(field).map((option, i) => (
                    <FormControlLabel
                      key={`${option.value}-${i}`}
                      value={String(option.value ?? "")}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {helperText && <FormHelperText>{helperText}</FormHelperText>}
              </FormControl>
            );

          case "textarea":
            return (
              <TextField
                {...input}
                fullWidth
                multiline
                minRows={Number(field.rows) || 3}
                maxRows={10}
                label={label}
                placeholder={field.placeholder}
                error={!!error}
                helperText={helperText}
              />
            );

          case "number":
            return (
              <TextField
                {...input}
                fullWidth
                type="number"
                label={label}
                placeholder={field.placeholder}
                inputProps={{ min: field.min, max: field.max, step: field.step }}
                error={!!error}
                helperText={helperText}
              />
            );

          case "date":
            return (
              <TextField
                {...input}
                fullWidth
                type="date"
                label={label}
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={helperText}
              />
            );

          default:
            // text (and its subtypes), hidden and anything else a text box can carry
            if (field.type === "hidden") return null;
            return (
              <TextField
                {...input}
                fullWidth
                type={["email", "tel", "password"].includes(field.subtype) ? field.subtype : "text"}
                label={label}
                placeholder={field.placeholder}
                inputProps={{ maxLength: field.maxlength || undefined }}
                error={!!error}
                helperText={helperText}
              />
            );
        }
      }}
    />
  );
};

/**
 * Renders a FormBuilder form for the request page. The answers live in the parent's react-hook-form
 * under `dynamic.f<index>`; `onLoaded` hands the loaded definition up so the parent can build the
 * payload with buildUserData at submit time.
 */
const DynamicFormComponent = ({ formId, control, errors, onLoaded }) => {
  const [form, setForm] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setForm(null);
    setLoadFailed(false);
    onLoaded?.(null);

    axiosInstance
      .get(`/DynamicForm/${formId}`)
      .then((data) => {
        if (cancelled) return;
        if (!data || !Array.isArray(data.fields)) throw new Error("invalid form");
        setForm(data);
        onLoaded?.(data);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [formId]);

  if (loadFailed) {
    return <Alert severity="error">تعذر تحميل نموذج الطلب، يرجى المحاولة لاحقاً</Alert>;
  }
  if (!form) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          // grows with the form name instead of wrapping it; 300 stays the minimum on desktop
          width: isMobile ? "100%" : "fit-content",
          minWidth: isMobile ? undefined : 300,
          maxWidth: "100%",
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
          {form.name || "تفاصيل الطلب"}
        </Typography>
      </Box>
      <Paper elevation={1} sx={{ p: 3, borderRadius: "12px", border: "1px solid #e0e0e0" }}>
        <Grid container spacing={3}>
          {form.fields.map((field, index) => (
            <Grid item xs={12} sm={["textarea", "header", "paragraph"].includes(field.type) ? 12 : 6} key={`${field.name || field.type}-${index}`}>
              {isInputField(field) ? (
                <InputField field={field} index={index} control={control} errors={errors} />
              ) : field.type === "header" || field.type === "paragraph" ? (
                <StaticField field={field} />
              ) : null}
            </Grid>
          ))}
        </Grid>
      </Paper>
    </>
  );
};

export default DynamicFormComponent;
